import { Trans, useLingui } from '@lingui/react'
import {
  RxByPrincipleReasonSet,
  RxByPrincipleSelectableReason,
} from '@redteclab/api/clients/bully'
import { clsx } from 'clsx'
import { type FormEventHandler, useCallback, useState, type VFC } from 'react'

import {
  getSelectedReasonState,
  SELECTABLE_REASON_INPUT_NAME,
} from 'views/assets/scripts/rxByPrincipleModal'
import Button from 'views/components/atoms/Button'
import InputCheckboxOrRadio from 'views/components/molecules/InputCheckboxOrRadio'
import Modal, { type ModalProps } from 'views/components/molecules/Modal'
import Notification from 'views/components/molecules/Notification'

import rxByPrinciplePills from './rx-by-principle-pills.svg'

import './RxByPrincipleModal.scss'

export type RxByPrincipleModalProps = Omit<ModalProps, 'children' | 'id'> & {
  onReasonSelect?: (reason: RxByPrincipleSelectableReason) => void
  productName: string
  reasonSet: RxByPrincipleReasonSet
}

const selectableReasonsByReasonSet: Record<
  RxByPrincipleReasonSet,
  RxByPrincipleSelectableReason[]
> = {
  [RxByPrincipleReasonSet.Dextromethorfan]: [
    RxByPrincipleSelectableReason.DryCough,
  ],
  [RxByPrincipleReasonSet.Paracetamol]: [
    RxByPrincipleSelectableReason.Fever,
    RxByPrincipleSelectableReason.Pain,
    RxByPrincipleSelectableReason.Arthritis,
  ],
  [RxByPrincipleReasonSet.PseudoEfedrine]: [
    RxByPrincipleSelectableReason.NasalBlockage,
  ],
  [RxByPrincipleReasonSet.ParacetamolIbuprofen]: [
    RxByPrincipleSelectableReason.ShortTermTreatmentMildPain,
  ],
  [RxByPrincipleReasonSet.ParacetamolPseudoEfedrine]: [
    RxByPrincipleSelectableReason.FeverAndHeadache,
    RxByPrincipleSelectableReason.NasalBlockage,
  ],
}

const RxByPrincipleModal: VFC<RxByPrincipleModalProps> = ({
  className,
  onReasonSelect,
  productName,
  reasonSet,
  ...rest
}) => {
  const { i18n } = useLingui()

  const headerReasonSet: Record<RxByPrincipleReasonSet, string> = {
    [RxByPrincipleReasonSet.Dextromethorfan]: i18n._(
      'rxByPrincipleModal.headerText.reasonSet.dextromethorfan'
    ),
    [RxByPrincipleReasonSet.Paracetamol]: i18n._(
      'rxByPrincipleModal.headerText.reasonSet.paracetamol'
    ),
    [RxByPrincipleReasonSet.PseudoEfedrine]: i18n._(
      'rxByPrincipleModal.headerText.reasonSet.pseudoEfedrine'
    ),
    [RxByPrincipleReasonSet.ParacetamolIbuprofen]: i18n._(
      'rxByPrincipleModal.headerText.reasonSet.paracetamolIbuprofen'
    ),
    [RxByPrincipleReasonSet.ParacetamolPseudoEfedrine]: i18n._(
      'rxByPrincipleModal.headerText.reasonSet.paracetamolPseudoEfedrine'
    ),
  }

  const reasonLabels: Record<RxByPrincipleSelectableReason, string> = {
    [RxByPrincipleSelectableReason.Arthritis]: i18n._(
      'rxByPrincipleModal.reason.arthritis'
    ),
    [RxByPrincipleSelectableReason.DryCough]: i18n._(
      'rxByPrincipleModal.reason.dryCough'
    ),
    [RxByPrincipleSelectableReason.Fever]: i18n._(
      'rxByPrincipleModal.reason.fever'
    ),
    [RxByPrincipleSelectableReason.NasalBlockage]: i18n._(
      'rxByPrincipleModal.reason.nasalBlockage'
    ),
    [RxByPrincipleSelectableReason.Pain]: i18n._(
      'rxByPrincipleModal.reason.pain'
    ),
    [RxByPrincipleSelectableReason.FeverAndHeadache]: i18n._(
      'rxByPrincipleModal.reason.feverAndHeadache'
    ),
    [RxByPrincipleSelectableReason.ShortTermTreatmentMildPain]: i18n._(
      'rxByPrincipleModal.reason.shortTermTreatmentMildPain'
    ),
  }

  const confirmationDanger: Record<RxByPrincipleReasonSet, string> = {
    [RxByPrincipleReasonSet.Dextromethorfan]: i18n._(
      'rxByPrincipleModal.confirmation.danger.dextromethorfan'
    ),
    [RxByPrincipleReasonSet.Paracetamol]: i18n._(
      'rxByPrincipleModal.confirmation.danger.paracetamol'
    ),
    [RxByPrincipleReasonSet.PseudoEfedrine]: i18n._(
      'rxByPrincipleModal.confirmation.danger.pseudoEfedrine'
    ),
    [RxByPrincipleReasonSet.ParacetamolIbuprofen]: i18n._(
      'rxByPrincipleModal.confirmation.danger.paracetamolIbuprofen'
    ),
    [RxByPrincipleReasonSet.ParacetamolPseudoEfedrine]: i18n._(
      'rxByPrincipleModal.confirmation.danger.paracetamolPseudoEfedrine'
    ),
  }

  const [reasonRequiredErrorMessage, setReasonRequiredErrorMessage] =
    useState(false)

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    event => {
      event.preventDefault()

      const { value, valid } = getSelectedReasonState(event.currentTarget)

      setReasonRequiredErrorMessage(!valid)

      if (onReasonSelect && valid && value) {
        onReasonSelect(value)
      }
    },
    [onReasonSelect]
  )

  return (
    <Modal
      className={clsx('o-RxByPrincipleModal', className)}
      id="rx-by-principle-modal"
      title={i18n._('rxByPrincipleModal.title')}
      {...rest}
    >
      <form
        className="o-RxByPrincipleModal__content"
        data-qa-id="rx-by-principle-form"
        noValidate
        onSubmit={onSubmit}
      >
        <header className="o-RxByPrincipleModal__header">
          <img
            alt=""
            className="o-RxByPrincipleModal__header-image"
            height={64}
            src={rxByPrinciplePills}
            width={64}
          />
          <p className="o-RxByPrincipleModal__header-text">
            <Trans
              id="rxByPrincipleModal.headerText"
              values={{ reasonSet: headerReasonSet[reasonSet] }}
            />
          </p>
        </header>
        <div className="o-RxByPrincipleModal__info">
          <Trans
            id="rxByPrincipleModal.info"
            components={{
              b: <span className="u-font-weight--bold" />,
              p: <p />,
            }}
            values={{
              productName: (
                <span className="o-RxByPrincipleModal__product-name">
                  {productName}
                </span>
              ),
            }}
          />
        </div>
        <fieldset className="o-RxByPrincipleModal__fieldset">
          <span className="o-RxByPrincipleModal__legend">
            <Trans id="rxByPrincipleModal.legend" />
          </span>
          <div className="o-RxByPrincipleModal__options">
            {selectableReasonsByReasonSet[reasonSet].map(reason => (
              <InputCheckboxOrRadio
                key={reason}
                data-qa-id={`rx-by-principle-reason-${reason}`}
                id={`rx-by-principle-reason-${reason}`}
                name={SELECTABLE_REASON_INPUT_NAME}
                type="radio"
                value={reason}
                required
              >
                {reasonLabels[reason]}
              </InputCheckboxOrRadio>
            ))}
          </div>
          <Notification
            className={clsx('o-RxByPrincipleModal__error-message', {
              /**
               * Use class to hide error message so validation could work with pure JavaScript approach
               * in file `app/views/assets/scripts/rxByPrincipleModal.ts`
               */
              'o-RxByPrincipleModal__error-message--hidden':
                !reasonRequiredErrorMessage,
            })}
            label={i18n._('rxByPrincipleModal.reasonRequiredErrorMessage')}
            type="error"
          />
        </fieldset>
        <footer className="o-RxByPrincipleModal__footer">
          <p>
            <Trans id="rxByPrincipleModal.confirmation.usage" />
          </p>
          <p>{confirmationDanger[reasonSet]}</p>
        </footer>
        <Button
          className="o-RxByPrincipleModal__submit-button"
          data-qa-id="rx-by-principle-submit"
          type="submit"
        >
          <Trans id="rxByPrincipleModal.submit" />
        </Button>
      </form>
    </Modal>
  )
}

export default RxByPrincipleModal
